import React, { useCallback, useMemo } from 'react';

import { Grid2 as Grid, styled } from '@mui/material';

import { Language } from '@mewa/types';
import { FixedColumnSlider, ResponsiveGrid, useSliderViewport } from '@mewa/ui';

import { SearchPageTranslations } from '../../components/header/types';
import {
  BlogArticlesSearchResultMeta,
  PageFindResultData,
} from '../../components/search/types';
import { ArticleTeaserMedium } from '../blog/components/article-teaser-medium';
import { StrapiImageData } from '../dynamic-content-page/content-components/image';

export const SearchResultBlogArticles = React.memo(
  ({
    results,
    expanded,
    ...context
  }: {
    results: PageFindResultData<BlogArticlesSearchResultMeta>[];
    translations: SearchPageTranslations;
    expanded: boolean;
    lang: Language;
    langPrefix: string;
    locale: string;
    blogSlug: string;
  }) => {
    const sliderViewport = useSliderViewport();

    const renderSlide = useCallback(
      (
        result: PageFindResultData<BlogArticlesSearchResultMeta>,
        idx: number,
      ) => (
        <Container key={idx}>
          <BlogArticle result={result} {...context} />
        </Container>
      ),
      [context],
    );

    const sliderView = useMemo(
      () => (
        <FixedColumnSlider
          slides={results}
          viewport={sliderViewport}
          renderSlide={renderSlide}
          withCardSpacing={true}
        />
      ),
      [results, sliderViewport, renderSlide],
    );

    const gridView = useMemo(
      () => (
        <ResponsiveGrid>
          {results.map((result, index) => (
            <Grid
              key={index}
              size={{
                xs: 12,
                sm: 6,
                md: 4,
              }}
            >
              <BlogArticle {...context} result={result} />
            </Grid>
          ))}
        </ResponsiveGrid>
      ),
      [results, context],
    );

    return expanded ? gridView : sliderView;
  },
);

const BlogArticle = ({
  lang,
  locale,
  langPrefix,
  blogSlug,
  result,
  translations,
}: {
  lang: Language;
  langPrefix: string;
  locale: string;
  blogSlug: string;
  result: PageFindResultData<BlogArticlesSearchResultMeta>;
  translations: SearchPageTranslations;
}) => {
  return (
    <ArticleTeaserMedium
      translations={translations}
      blogSlug={blogSlug}
      locale={locale}
      langPrefix={langPrefix}
      lang={lang}
      article={{
        author: {
          name: result.meta.author,
          image: null as unknown as StrapiImageData,
          position: '',
        },
        responsiveCoverImage: {
          imageDesktop: {
            fields: { assetUrl: result.meta.image ?? '' },
            height: 0,
            width: 0,
          },
          imageMobile: null,
          imageAlternativeText: result.meta.title,
        },
        date: result.meta.date,
        excerpt: '',
        title: result.meta.title,
      }}
    />
  );
};

const Container = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('sm')]: {
    margin: '10px 0 10px',
  },
}));
